import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissions(null);
      setUserRoles([]);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/permissions`, { headers: getAuthHeader() });
      setPermissions(res.data.permissions);
      setUserRoles(res.data.roles);
    } catch {
      setPermissions(null);
      setUserRoles([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const refresh = () => fetchPermissions();

  /**
   * Check if user can access a module.
   * @param {string} module - e.g., 'personnel_database', 'equipment_planner'
   * @param {string} [tab] - optional tab e.g., 'general', 'finance'
   * @returns {'edit'|'view'|null}
   */
  const getAccess = (module, tab) => {
    if (!permissions) return null;
    const moduleAccess = permissions[module];
    if (!moduleAccess) return null;
    if (typeof moduleAccess === 'string') return moduleAccess;
    if (tab) return moduleAccess[tab] || null;
    // If no tab specified, return highest access across all tabs
    const values = Object.values(moduleAccess);
    if (values.includes('edit')) return 'edit';
    if (values.includes('view')) return 'view';
    return null;
  };

  const canEdit = (module, tab) => {
    if (loading || !permissions) return true; // Allow all during loading
    return getAccess(module, tab) === 'edit';
  };
  const canView = (module, tab) => {
    if (loading || !permissions) return true; // Allow all during loading
    const access = getAccess(module, tab);
    return access === 'edit' || access === 'view';
  };

  return (
    <PermissionsContext.Provider value={{ 
      permissions, userRoles, loading, refresh: fetchPermissions, 
      getAccess, canEdit, canView 
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Fallback for when provider isn't mounted yet - allow all
    return {
      permissions: null,
      userRoles: [],
      loading: true,
      refresh: () => {},
      getAccess: () => 'edit',
      canEdit: () => true,
      canView: () => true
    };
  }
  return ctx;
}
